import { useCallback, useEffect, useState } from "react";
import {
  ISharedFullConversation,
  IConversationSummary,
} from "atlas-shared/dist/interfaces/entities/conversation.interface";
import { fetchConversationSummaries, summarizeConversation } from "@Api";
import { getCreatedDate } from "@Utils";
import { useAsync } from "@Hooks";

export const useConversationSummary = (conversation: ISharedFullConversation) => {
  const [newSummary, setNewSummary] = useState<IConversationSummary | null>(null);
  const [newSummaryLoading, setNewSummaryLoading] = useState(false);
  const [newSummaryError, setNewSummaryError] = useState<string | null>(null);
  const [isSummaryOutdated, setIsSummaryOutdated] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const {
    data: summaries,
    loading: summariesLoading,
    error: summariesError,
  } = useAsync(async () => {
    const summariesData = await fetchConversationSummaries(conversation.id);
    return summariesData.reverse();
  }, "SUMMERY_" + conversation.id);

  const latestConversationMessageDate = getCreatedDate(conversation.messages?.[0]);
  const latestSummaryDate = getCreatedDate(summaries?.[0]);

  useEffect(() => {
    if (!latestConversationMessageDate) return;

    setIsSummaryOutdated(
      latestSummaryDate !== null && latestConversationMessageDate > latestSummaryDate
    );
  }, [latestConversationMessageDate, latestSummaryDate]);

  const getConversationSummary = useCallback(async () => {
    setNewSummaryLoading(true);
    try {
      const summaryData = await summarizeConversation(conversation.id);
      setNewSummary(summaryData);
    } catch (error) {
      setNewSummaryError(error as string);
    } finally {
      setNewSummaryLoading(false);
    }
  }, [conversation.id]);

  return {
    getConversationSummary,
    newSummary,
    newSummaryLoading,
    newSummaryError,
    summaries,
    summariesLoading,
    summariesError,
    isSummaryOutdated,
    isOpen,
    setIsOpen,
  };
};
